import React, { useState } from 'react';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PolicyData {
  policy_info: {
    policy_number: string | null;
    carrier: string | null;
    effective_date: string | null;
    expiration_date: string | null;
    policy_type: string | null;
  };
  coverage: {
    business_interruption: boolean;
    extended_business_income: boolean;
    extra_expense: boolean;
    civil_authority: boolean;
  };
  limits: {
    aggregate_limit: number | null;
    per_occurrence_limit: number | null;
    time_limit_months: number | null;
  };
  sublimits: {
    extended_period_indemnity_days: number | null;
    extra_expense_limit: number | null;
    civil_authority_limit: number | null;
  };
  deductibles: {
    time_deductible_hours: number | null;
    time_deductible_days: number | null;
    monetary_deductible: number | null;
  };
  exclusions: Array<{
    type: string;
    description: string;
    severity: string;
    applies_to_bi: boolean;
  }>;
  warnings: string[];
  confidence: number;
}

interface PolicyUploadProps {
  reportId?: string;
  onPolicyUploaded?: (data: PolicyData) => void;
  onClose?: () => void;
}

export default function PolicyUpload({ reportId, onPolicyUploaded, onClose }: PolicyUploadProps) {
  const { getToken } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [policyData, setPolicyData] = useState<PolicyData | null>(null);
  const [error, setError] = useState('');

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.pdf')) {
        setSelectedFile(file);
        setError('');
      } else {
        setError('Please select a PDF file');
      }
    }
  };

  const uploadPolicy = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', selectedFile);

      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent: any) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      };

      const url = reportId 
        ? `${BACKEND_URL}/api/v1/policy/upload?report_id=${reportId}`
        : `${BACKEND_URL}/api/v1/policy/upload`;

      const response = await axios.post(url, formData, config);

      setPolicyData(response.data.policy_data);
      setIsUploading(false);
      
      if (onPolicyUploaded) {
        onPolicyUploaded(response.data.policy_data);
      }
    } catch (err: any) {
      console.error('Policy upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed. Please try again.');
      setIsUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border-2 border-blue-200 p-6 shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-3xl">🏛️</span>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Policy Upload</h3>
            <p className="text-sm text-gray-600">We'll read your policy for you</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {!policyData ? (
        <>
          {/* Upload Area */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-4">
            {selectedFile ? (
              <div>
                <div className="text-4xl mb-2">📄</div>
                <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-600 mt-1">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-2">🏛️</div>
                <p className="font-semibold text-gray-900 mb-2">Upload Policy PDF</p>
                <p className="text-sm text-gray-600">
                  We'll extract limits, deductibles, and exclusions
                </p>
              </div>
            )}
          </div>

          <input
            type="file"
            accept=".pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="policy-file-input"
          />

          {!selectedFile && (
            <label
              htmlFor="policy-file-input"
              className="block w-full py-3 bg-blue-600 text-white rounded-lg font-semibold text-center cursor-pointer hover:bg-blue-700 transition"
            >
              Choose Policy PDF
            </label>
          )}

          {selectedFile && !isUploading && (
            <button
              onClick={uploadPolicy}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-blue-700 transition"
            >
              🚀 Parse Policy
            </button>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-gray-700">Parsing policy...</span>
                <span className="text-blue-600 font-bold">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-gradient-to-r from-blue-600 to-blue-600 h-full rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-gray-600 text-center">
                Extracting limits, deductibles, and exclusions...
              </p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">⚠️ {error}</p>
            </div>
          )}
        </>
      ) : (
        /* Results View */
        <div className="space-y-4">
          {/* Success Banner */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-bold text-green-900">Policy Parsed Successfully</p>
                <p className="text-sm text-green-700">
                  Confidence: {policyData.confidence}%
                </p>
              </div>
            </div>
          </div>

          {/* Coverage Limits */}
          {policyData.limits.aggregate_limit && (
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span>💰</span> Coverage Limits
              </h4>
              <div className="space-y-2">
                {policyData.limits.aggregate_limit && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Aggregate Limit:</span>
                    <span className="font-bold text-gray-900">
                      ${policyData.limits.aggregate_limit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {policyData.limits.per_occurrence_limit && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Per Occurrence:</span>
                    <span className="font-bold text-gray-900">
                      ${policyData.limits.per_occurrence_limit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {policyData.limits.time_limit_months && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Time Limit:</span>
                    <span className="font-bold text-gray-900">
                      {policyData.limits.time_limit_months} months
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Deductibles */}
          {(policyData.deductibles.time_deductible_days || policyData.deductibles.monetary_deductible) && (
            <div className="bg-amber-50 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span>⏱️</span> Deductibles
              </h4>
              <div className="space-y-2">
                {policyData.deductibles.time_deductible_days && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Waiting Period:</span>
                    <span className="font-bold text-gray-900">
                      {policyData.deductibles.time_deductible_days} days
                    </span>
                  </div>
                )}
                {policyData.deductibles.monetary_deductible && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Monetary Deductible:</span>
                    <span className="font-bold text-gray-900">
                      ${policyData.deductibles.monetary_deductible.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Exclusions */}
          {policyData.exclusions && policyData.exclusions.length > 0 && (
            <div className="bg-red-50 rounded-lg p-4">
              <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span>🚫</span> Exclusions
              </h4>
              <div className="space-y-2">
                {policyData.exclusions.map((exclusion, idx) => (
                  <div key={idx} className="text-sm">
                    <span className={`font-bold ${
                      exclusion.severity === 'HIGH' ? 'text-red-700' :
                      exclusion.severity === 'MEDIUM' ? 'text-amber-700' :
                      'text-gray-700'
                    }`}>
                      {exclusion.type}
                    </span>
                    {exclusion.applies_to_bi && (
                      <span className="ml-2 text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded">
                        Applies to BI
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Warnings */}
          {policyData.warnings && policyData.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-yellow-900 mb-1">⚠️ Warnings:</p>
              <ul className="text-xs text-yellow-800 space-y-1">
                {policyData.warnings.map((warning, idx) => (
                  <li key={idx}>• {warning}</li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}
